import { Type } from '@sinclair/typebox';
import { AgentStreamMessage } from './envelope.js';
import { HistoryPage, TimelineRequest } from './history.js';
import { InteractionRequestedMessage, InteractionResolvedMessage, InteractionResponseMessage, } from './interactions.js';
import { ResourceRequest, ResourceResponse, ResourceUpdate, TimelineResourceBindingReplacement, } from './resources.js';
import { AgentPersistenceHandle, AgentSnapshot } from './snapshot.js';
import { ProtocolVersionSchema } from './version.js';
import { ListCommandsRequest, ExecuteCommandRequest, CommandListResponse, CommandResultResponse } from './commands.js';
const NonEmptyString = Type.String({ minLength: 1 });
const Strict = (properties) => Type.Object(properties, { additionalProperties: false });
export const NegotiateRequest = Strict({
    protocolVersion: ProtocolVersionSchema,
    type: Type.Literal('negotiate'),
});
export const NegotiateResponse = Strict({
    protocolVersion: ProtocolVersionSchema,
    type: Type.Literal('negotiated'),
});
export const AgentProviderDescriptor = Strict({
    providerId: NonEmptyString,
    displayName: NonEmptyString,
});
export const ProviderListResponse = Strict({
    protocolVersion: ProtocolVersionSchema,
    type: Type.Literal('provider_list'),
    payload: Strict({ providers: Type.Array(AgentProviderDescriptor) }),
});
export const SendMessageRequest = Strict({
    protocolVersion: ProtocolVersionSchema,
    type: Type.Literal('send_message'),
    payload: Strict({ requestId: NonEmptyString, agentId: NonEmptyString, text: Type.String(), delivery: Type.Optional(Type.Union([Type.Literal('immediate'), Type.Literal('next_turn')])) }),
});
export const SteerRequest = Strict({
    protocolVersion: ProtocolVersionSchema,
    type: Type.Literal('steer'),
    payload: Strict({ requestId: NonEmptyString, agentId: NonEmptyString, text: Type.String() }),
});
export const CancelRequest = Strict({
    protocolVersion: ProtocolVersionSchema,
    type: Type.Literal('cancel'),
    payload: Strict({ requestId: NonEmptyString, agentId: NonEmptyString }),
});
export const SetPlanningRequest = Strict({
    protocolVersion: ProtocolVersionSchema,
    type: Type.Literal('set_planning'),
    payload: Strict({ requestId: NonEmptyString, agentId: NonEmptyString, active: Type.Boolean() }),
});
export const SetSessionSettingRequest = Strict({
    protocolVersion: ProtocolVersionSchema,
    type: Type.Literal('set_session_setting'),
    payload: Strict({ requestId: NonEmptyString, agentId: NonEmptyString, settingId: NonEmptyString, value: NonEmptyString }),
});
export const AgentSessionConfig = Strict({
    sessionId: NonEmptyString,
    cwd: Type.Optional(NonEmptyString),
    model: Type.Optional(NonEmptyString),
    reasoningEffort: Type.Optional(NonEmptyString),
    systemPrompt: Type.Optional(Type.String()),
    planning: Type.Optional(Type.Boolean()),
});
export const CreateAgentRequest = Strict({
    protocolVersion: ProtocolVersionSchema,
    type: Type.Literal('create_agent'),
    payload: Strict({
        requestId: NonEmptyString,
        agentId: NonEmptyString,
        providerId: NonEmptyString,
        config: AgentSessionConfig,
    }),
});
export const ResumeAgentRequest = Strict({
    protocolVersion: ProtocolVersionSchema,
    type: Type.Literal('resume_agent'),
    payload: Strict({
        requestId: NonEmptyString,
        agentId: NonEmptyString,
        persistence: AgentPersistenceHandle,
    }),
});
export const AgentSessionResponse = Strict({
    protocolVersion: ProtocolVersionSchema,
    type: Type.Literal('agent_session'),
    payload: Strict({
        requestId: NonEmptyString,
        agentId: NonEmptyString,
        providerId: NonEmptyString,
        sessionId: NonEmptyString,
        persistence: Type.Optional(AgentPersistenceHandle),
    }),
});
export const CommandAcknowledgementMessage = Strict({
    protocolVersion: ProtocolVersionSchema,
    type: Type.Literal('command_acknowledged'),
    payload: Strict({
        requestId: NonEmptyString,
        agentId: NonEmptyString,
        command: Type.Union([
            Type.Literal('send_message'), Type.Literal('steer'), Type.Literal('cancel'), Type.Literal('set_planning'), Type.Literal('set_session_setting'),
        ]),
    }),
});
export const TimelineSubscriptionRequest = Strict({
    protocolVersion: ProtocolVersionSchema,
    type: Type.Literal('timeline_subscription'),
    payload: Strict({ requestId: NonEmptyString, agentIds: Type.Array(NonEmptyString, { uniqueItems: true }) }),
});
export const TimelineSubscriptionResponse = Strict({
    protocolVersion: ProtocolVersionSchema,
    type: Type.Literal('timeline_subscribed'),
    payload: Strict({ requestId: NonEmptyString, agentIds: Type.Array(NonEmptyString, { uniqueItems: true }) }),
});
export const TimelineReplacementMessage = Strict({
    protocolVersion: ProtocolVersionSchema,
    type: Type.Literal('timeline_replacement'),
    payload: Strict({ agentId: NonEmptyString, epoch: NonEmptyString }),
});
export const AgentUpdateMessage = Strict({
    protocolVersion: ProtocolVersionSchema,
    type: Type.Literal('agent_update'),
    payload: AgentSnapshot.properties.payload,
});
export const ProtocolErrorMessage = Strict({
    protocolVersion: ProtocolVersionSchema,
    type: Type.Literal('protocol_error'),
    payload: Strict({
        requestId: Type.Optional(NonEmptyString),
        code: NonEmptyString,
        message: Type.String(),
        recoverable: Type.Boolean(),
    }),
});
export const IncompatibleProtocolVersionErrorMessage = Strict({
    protocolVersion: NonEmptyString,
    type: Type.Literal('protocol_error'),
    payload: Strict({
        requestId: Type.Optional(NonEmptyString),
        code: Type.Literal('incompatible_protocol_version'),
        message: Type.String(),
        recoverable: Type.Literal(false),
    }),
});
export const ClientMessage = Type.Union([
    ListCommandsRequest,
    ExecuteCommandRequest,
    NegotiateRequest,
    CreateAgentRequest,
    ResumeAgentRequest,
    SendMessageRequest,
    SteerRequest,
    CancelRequest,
    SetPlanningRequest,
    SetSessionSettingRequest,
    TimelineSubscriptionRequest,
    TimelineRequest,
    InteractionResponseMessage,
    ResourceRequest,
]);
export const ServerMessage = Type.Union([
    CommandListResponse,
    CommandResultResponse,
    NegotiateResponse,
    ProviderListResponse,
    AgentSessionResponse,
    CommandAcknowledgementMessage,
    AgentSnapshot,
    AgentUpdateMessage,
    TimelineSubscriptionResponse,
    HistoryPage,
    TimelineReplacementMessage,
    AgentStreamMessage,
    InteractionRequestedMessage,
    InteractionResolvedMessage,
    ResourceResponse,
    ResourceUpdate,
    TimelineResourceBindingReplacement,
    ProtocolErrorMessage,
]);
//# sourceMappingURL=messages.js.map